<template>
    <div
        class="bg-black relative col-span-3 grid grid-cols-subgrid transition-opacity duration-300 gap-0 mt-4"
        :class="{ 'opacity-40': transparent || killed, ' scale-80 !mt-2': transparent, 'my-5': isActive }"
        :style="glowStyle"
    >
        <div class="absolute top-0 left-0 -translate-y-full text-xs uppercase font-bold flex playercard-clip" :style="{backgroundColor: color}">
            <div class="w-7 relative">
               <div class="w-7 h-7 absolute left-0 bottom-0 clip-left-bottom-corner-small" :style="{ backgroundColor: getLighterColor(color, 40) }">
                <div class="inset-px absolute clip-left-bottom-corner-small"  :style="{ backgroundColor: color }">
                    <img
                        v-if="player.avatar"
                        v-avatar-fallback="player.avatar"
                        :alt="$t('general.avatar_alt')"
                        class="w-full h-full object-cover absolute left-0 bottom-0"
                        />
                </div>
               </div> 
            </div>
            <span class="px-5">
                {{ player.tag_name }}
            </span>
        </div>
        <div class="p-2">
            <div
                class="clip-left-bottom-corner relative flex h-8 w-8 items-center justify-center overflow-hidden"
                :style="{ backgroundColor: getLighterColor(color, 40) }"
            >
                <div
                    class="clip-left-bottom-corner font-title absolute inset-px flex items-center justify-center text-xs font-bold"
                    :style="{ backgroundColor: color }"
                >
                    {{ targetSection }}
                    <svg v-if="isKiller" class="absolute w-full h-full" viewBox="0 0 30 30">
                        <g filter="url(#filter0_d_23038_20081)">
                        <path d="M20.4325 24.3278H15.7334V20.4158H13.4034V24.3278H8.70434V20.4158H4.38712C3.62342 20.4158 3 19.7846 3 19.0131V5.51598C3 4.74449 3.62342 4.11328 4.38712 4.11328H24.7419C25.5056 4.11328 26.129 4.74449 26.129 5.51598V19.0131C26.129 19.7846 25.5056 20.4158 24.7419 20.4158H20.4247V24.3278H20.4325ZM15.9828 24.0863H20.1909V20.1742H24.7497C25.3809 20.1742 25.8953 19.6521 25.8953 19.0209V5.52378C25.8953 4.88477 25.3809 4.37041 24.7497 4.37041H4.39492C3.7637 4.37041 3.24938 4.89256 3.24938 5.52378V19.0209C3.24938 19.6599 3.7637 20.1742 4.39492 20.1742H8.95369V24.0863H13.1618V20.1742H15.9828V24.0863Z" :fill="getLighterColor(color, 80)"/>
                        <path d="M3 4.27798V17.7751C3 18.4843 3.56886 19.0532 4.27801 19.0532H8.76667V22.9652H13.2709V19.0532H15.8737V22.9652H20.378V19.0532H24.8666C25.5758 19.0532 26.1446 18.4843 26.1446 17.7751V4.27798C26.1446 3.56884 25.5758 3 24.8666 3H4.28581C3.57666 3 3.0078 3.56884 3.0078 4.27798H3ZM11.837 12.024H5.51709V9.31216H11.837V12.024ZM12.8969 14.5177L14.5723 12.5072L16.2478 14.5177H12.8969ZM23.6276 12.024H17.3076V9.31216H23.6276V12.024Z" :fill="getLighterColor(color, 80)"/>
                        </g>
                        <defs>
                        <filter id="filter0_d_23038_20081" x="0" y="0" width="30" height="30" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                        <feOffset dx="7" dy="7"/>
                        <feGaussianBlur stdDeviation="5"/>
                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.75 0"/>
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_23038_20081"/>
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_23038_20081" result="shape"/>
                        </filter>
                        </defs>
                    </svg>
                </div>
            </div>
        </div>

        <!-- Score/Lives bars (3 slots) -->
        <div class="clip-diagonal-corners relative flex h-full w-full bg-zinc-700">
            <div
                class="bg-black clip-diagonal-corners absolute left-0.5 flex h-full w-[calc(100%-4px)] p-2"
            >
                <div
                    class="clip-diagonal-corners relative grid w-full grid-cols-3 gap-0.5 bg-zinc-900 pt-1"
                    :class="{ 'border-red-500': (showDeadState || killed) && filledBars === 0 }"
                >
                    <!-- Killed overlay -->
                    <div
                        v-if="killed"
                        class="absolute inset-0 z-10 flex items-center justify-center bg-red-900/60"
                    >
                        <span class="text-sm font-bold tracking-wider text-red-200 uppercase"
                            >{{ $t('killer.killed') }}</span
                        >
                    </div>
                    <div
                        v-for="barIndex in 3"
                        :key="barIndex"
                        class="flex-1 bg-zinc-700 transition-all"
                        :style="barIndex <= filledBars ? getGradientStyle(barIndex) : {}"
                    ></div>
                </div>
            </div>
        </div>
        <!-- Plus/Minus buttons (for own score bar) -->
        <div class="flex h-12 gap-1 pl-1" v-if="mode === 'score'">
            <div
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm font-bold text-black transition-colors"
                :class="buttonClasses"
                data-testid="killer-plus"
                @click="canInteract ? $emit('click-plus') : null"
            >
                <img
                    src="@/assets/images/games/killer/plus.svg"
                    :alt="$t('killer.plus_icon_alt')"
                />
            </div>
            <div
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm font-bold text-black transition-colors"
                :class="minusButtonClasses"
                data-testid="killer-minus"
                @click="canRemove ? $emit('click-minus') : null"
            >
                 <img
                    src="@/assets/images/games/killer/minus.svg"
                    :alt="$t('killer.minus_icon_alt')"
                />
            </div>
        </div>

        <template v-if="mode === 'kill'">
            <div
                class="flex h-12 w-full items-center justify-center rounded-sm text-sm font-bold text-black uppercase transition-colors"
                :class="buttonClasses"
                data-testid="killer-kill"
                @click="canInteract ? $emit('click-kill') : null"
            >
                 <img
                    src="@/assets/images/games/killer/kill.svg"
                    :alt="$t('killer.kill_icon_alt')"
                    class="h-full"
                />
            </div>
        </template>
    </div>
</template>

<script>
import { getDarkerColor, getLighterColor } from '@/utils/colorHelpers'

export default {
    name: 'PlayerScoreBar',
    props: {
        player: {
            type: Object,
            required: true,
        },
        color: {
            type: String,
            default: '#FFFFFF',
        },
        filledBars: {
            type: Number,
            default: 0,
        },
        /** 'score' = plus/minus buttons, 'kill' = single kill button, 'none' = no buttons */
        mode: {
            type: String,
            default: 'none',
            validator: (v) => ['score', 'kill', 'none'].includes(v),
        },
        canInteract: {
            type: Boolean,
            default: false,
        },
        canRemove: {
            type: Boolean,
            default: false,
        },
        transparent: {
            type: Boolean,
            default: false,
        },
        showDeadState: {
            type: Boolean,
            default: false,
        },
        killed: {
            type: Boolean,
            default: false,
        },
        isActive: {
            type: Boolean,
            default: false,
        },
        targetSection: {
            type: [String, Number],
            default: null,
        },
        isKiller: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['click-plus', 'click-minus', 'click-kill'],
    computed: {
        buttonClasses() {
            if (this.killed) {
                return 'cursor-not-allowed opacity-50'
            }
            if (this.canInteract) {
                return 'cursor-pointer'
            }
            if (this.showDeadState && this.filledBars === 0) {
                return 'cursor-not-allowed opacity-50'
            }
            return 'cursor-not-allowed opacity-50'
        },
        minusButtonClasses() {
            if (this.canRemove) {
                return 'cursor-pointer hover:bg-gray-100'
            }
            return 'cursor-not-allowed opacity-50'
        },
        glowStyle() {
            if (!this.isActive) return {}
            return {
                boxShadow: `0 0 20px 4px ${this.color}`,
            }
        },
    },
    methods: {
        getLighterColor,
        getDarkerColor,
        getGradientStyle(index) {
            const endColor = this.getLighterColor(this.color, 40 - index * 5)
            const startColor = this.getDarkerColor(this.color, 10 + index * 5)
            return {
                background: `linear-gradient(90deg, ${startColor} 0%, ${endColor} 100%)`,
            }
        },
    },
}
</script>


<style lang="scss">
    .playercard-clip{
        clip-path: polygon(
            0 -100px, 
            0 calc(100% - 7px), 
            7px 100%, 
            100% 100%, 
            calc(100% - 15px) 0,
            100% 0);
        padding-right: 25px;
    }
</style>